import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { departmentsAPI, branchesAPI, QUERY_KEYS } from '@/api';
import { Card, CardContent, Button, Input, Badge, Modal } from '@/components/ui';
import { BranchFilterSelect, LoadingSpinner, Pagination } from '@/components/common';
import { Building, Plus, Edit2, Trash2, Users } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { extractPaginatedData } from '@/utils/pagination';

export default function DepartmentsPage() {
  const perPage = 6;
  const { can } = useAuth();
  const canManage = can('manage_departments');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingDept, setEditingDept] = useState<any>(null);
  const [branchFilter, setBranchFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: departmentsData, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.departments(), branchFilter, page],
    queryFn: () => departmentsAPI.list({ branch_id: branchFilter, page }),
  });

  const { data: branchesData } = useQuery({
    queryKey: QUERY_KEYS.branches(),
    queryFn: () => branchesAPI.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => departmentsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments() });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => departmentsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments() });
      setEditingDept(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => departmentsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments() });
    },
  });

  const { items: departments, meta } = extractPaginatedData<any>(departmentsData, page, perPage);
  const branches = Array.isArray(branchesData?.data)
    ? branchesData.data
    : branchesData?.data?.data || [];

  const handleDelete = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этот отдел?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Отделы</h1>
        {canManage && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Добавить отдел
          </Button>
        )}
      </div>

      <Modal
        isOpen={showForm || !!editingDept}
        onClose={() => {
          setShowForm(false);
          setEditingDept(null);
        }}
        title={editingDept ? 'Редактировать отдел' : 'Новый отдел'}
      >
        <DepartmentForm
          initialData={editingDept}
          branches={branches}
          onSubmit={async (data: any) => {
            if (editingDept) {
              await updateMutation.mutateAsync({ id: editingDept.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingDept(null);
          }}
        />
      </Modal>
      {/* Filter */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <BranchFilterSelect
              alwaysShow
              className="px-4 py-2 border rounded-lg flex-1"
              value={branchFilter}
              onChange={(value) => {
                setPage(1);
                setBranchFilter(value);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Departments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {departments.map((dept: any) => (
          <Card key={dept.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Building className="w-8 h-8 text-indigo-600" />
                  <div>
                    <h3 className="font-semibold">{dept.name}</h3>
                    <p className="text-sm text-gray-500">{dept.code}</p>
                  </div>
                </div>
                <Badge variant={dept.is_active ? 'success' : 'secondary'}>
                  {dept.is_active ? 'Активен' : 'Неактивен'}
                </Badge>
              </div>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-500">Филиал:</span>
                  <span>{dept.branch?.name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Сотрудники:</span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {dept.employees_count || 0}
                  </span>
                </div>
                {dept.description && (
                  <p className="text-gray-600 text-xs mt-2">{dept.description}</p>
                )}
              </div>

              {canManage && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingDept(dept)}
                    className="flex-1"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Редактировать
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(dept.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <Pagination meta={meta} onPageChange={setPage} />

      {departments.length === 0 && (
        <div className="text-center py-12">
          <Building className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Отделы не найдены</h3>
          <p className="text-gray-500">Создайте отдел для организации сотрудников.</p>
        </div>
      )}
    </div>
  );
}

function DepartmentForm({
  initialData,
  branches,
  onSubmit,
  onCancel,
}: {
  initialData?: any;
  branches: any[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    code: initialData?.code || '',
    branch_id: initialData?.branch_id || '',
    description: initialData?.description || '',
    is_active: initialData?.is_active ?? true,
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          placeholder="Название отдела"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <Input
          placeholder="Код (например, IT, HR, SALES)"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value })}
        />
      </div>
      <select
        className="w-full px-4 py-2 border rounded-lg"
        value={formData.branch_id}
        onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
      >
        <option value="">Выберите филиал</option>
        {branches.map((branch: any) => (
          <option key={branch.id} value={branch.id}>
            {branch.name}
          </option>
        ))}
      </select>
      <Input
        placeholder="Описание (опционально)"
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
      />
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={() => onSubmit(formData)} className="flex-1">
          Сохранить
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Отмена
        </Button>
      </div>
    </div>
  );
}

